import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../fonts/breuer-headline.css';
import './thankyou.css';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectAdminToken } from '../../admin/sessionSlice';
import { getDbUserId } from '../../functions/multiplayUserId';
import { setBackButtonUrl } from '../uiSlice';
import charSlider from '../images/stage2/flapy/Char1.png';
import dumbledoreImg from '../images/AlbusDumbledore.png';

/** Max flight points (must match `BroomFlightGame.jsx`). */
const MAX_FLIGHT_SCORE = 150;

/** Static spell-stage cap for performance tier (welcome spellcasting trial scale). */
const MAX_SPELL_SCORE = 150;

const FEEDBACK_MIN = 1;
const FEEDBACK_MAX = 5;

function readStoredUser() {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Resolve spell / flight / total from DB report and/or navigation state.
 * DB `stage1` = spell, `stage2` = flight (see backend fetchReport).
 */
function deriveScores(report, nav) {
  const navSpell = typeof nav?.spellScore === 'number' ? nav.spellScore : null;
  const navFlight = typeof nav?.flightScore === 'number' ? nav.flightScore : null;
  const navTotal = typeof nav?.points === 'number' ? nav.points : null;

  const s1Raw = report?.stage1;
  const s2Raw = report?.stage2;
  const s1 = s1Raw != null && s1Raw !== '' ? Number(s1Raw) : NaN;
  const s2 = s2Raw != null && s2Raw !== '' ? Number(s2Raw) : NaN;
  const totalDb = report?.points != null ? Number(report.points) : NaN;

  let spell = Number.isFinite(s1) ? s1 : navSpell;
  let flight = Number.isFinite(s2) ? s2 : navFlight;
  let total = Number.isFinite(totalDb) ? totalDb : navTotal;

  if (spell == null && flight == null) {
    if (total != null) {
      spell = total;
      flight = 0;
    } else {
      spell = 0;
      flight = 0;
      total = 0;
    }
  } else {
    if (spell == null) spell = total != null && flight != null ? Math.max(0, total - flight) : 0;
    if (flight == null) flight = total != null && spell != null ? Math.max(0, total - spell) : 0;
    if (total == null && spell != null && flight != null) total = spell + flight;
    if (total == null) total = (spell || 0) + (flight || 0);
  }

  return {
    spell: Math.max(0, Math.round(Number(spell) || 0)),
    flight: Math.max(0, Math.round(Number(flight) || 0)),
    total: Math.max(0, Math.round(Number(total) || 0)),
  };
}

function performanceTier(total, maxSpell, maxFlight) {
  const cap = maxSpell + maxFlight;
  if (cap <= 0) return 'mid';
  const r = total / cap;
  if (r >= 0.67) return 'high';
  if (r >= 0.34) return 'mid';
  return 'low';
}

function performanceCopy(tier) {
  if (tier === 'high') return 'Hogwarts remembers your brilliance.';
  if (tier === 'mid') return 'You have potential…';
  return 'Every wizard begins somewhere.';
}

function clampFeedbackStep(n) {
  const x = Math.round(Number(n) || FEEDBACK_MIN);
  return Math.min(FEEDBACK_MAX, Math.max(FEEDBACK_MIN, x));
}

function normStageId(v) {
  if (v == null || v === '') return '';
  return String(v).trim();
}

const EMPTY_NAV_STATE = {};

function ThankYou() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);

  const nav = location.state ?? EMPTY_NAV_STATE;
  const stored = readStoredUser();
  const sessionUser = user || stored;

  // const apiBase = useMemo(() => {
  //   const raw = String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  //   if (!raw) return '';
  //   if (raw.endsWith('/api') || raw.endsWith('/apiprod')) return raw;
  //   return `${raw}/api`;
  // }, []);
  const backendBase = process.env.REACT_APP_BACKEND_URL;

  const initial = useMemo(() => deriveScores(null, nav), [nav]);

  const [phase, setPhase] = useState('scores');
  const [spellScore, setSpellScore] = useState(initial.spell);
  const [flightScore, setFlightScore] = useState(initial.flight);
  const [stageRowId, setStageRowId] = useState(() => normStageId(nav.stageId));
  const [feedbackValue, setFeedbackValue] = useState(3);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackAlreadySubmitted, setFeedbackAlreadySubmitted] = useState(false);

  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const activePointerRef = useRef(null);

  useEffect(() => {
    if (user?.backButtonRedirect != null) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [user, dispatch]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const hadCommon = body.classList.contains('common-bg');
    html.classList.add('thank-you-static-bg');
    body.classList.add('thank-you-static-bg');
    body.classList.remove('common-bg');
    return () => {
      html.classList.remove('thank-you-static-bg');
      body.classList.remove('thank-you-static-bg');
      if (hadCommon) {
        body.classList.add('common-bg');
      }
    };
  }, []);

  useEffect(() => {
    const uid = getDbUserId(sessionUser);
    const sid = sessionUser?.sessionId;
    const oid = sessionUser?.organizationId;
    if (!uid || !sid || !oid || !adminToken ) return;

    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: String(uid),
            sessionId: String(sid),
            organizationId: String(oid),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success || !data.report) return;
        const { spell, flight } = deriveScores(data.report, nav);
        setSpellScore(spell);
        setFlightScore(flight);
        const rid = data.report.id;
        if (rid != null && rid !== '') {
          setStageRowId(String(rid));
        }
        const fb = data.report.feedback;
        if (fb != null && fb !== '' && Number.isFinite(Number(fb))) {
          setFeedbackValue(clampFeedbackStep(Number(fb)));
          setFeedbackAlreadySubmitted(true);
          setPhase('done');
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [sessionUser, adminToken, nav]);

  const totalPoints = spellScore + flightScore;
  const tier = useMemo(
    () => performanceTier(totalPoints, MAX_SPELL_SCORE, MAX_FLIGHT_SCORE),
    [totalPoints]
  );
  const perfText = useMemo(() => performanceCopy(tier), [tier]);

  const fillPercent = ((feedbackValue - FEEDBACK_MIN) / (FEEDBACK_MAX - FEEDBACK_MIN)) * 100;
  const handlePercent = fillPercent;

  const setValueFromClientX = useCallback((clientX) => {
    const el = trackRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width <= 0) return;
    const t = (clientX - r.left) / r.width;
    const step = clampFeedbackStep(Math.round(t * (FEEDBACK_MAX - FEEDBACK_MIN)) + FEEDBACK_MIN);
    setFeedbackValue(step);
  }, []);

  // Fix 1: preventDefault stops touch from triggering body scroll/stretch
  const onTrackPointerDown = useCallback(
    (e) => {
      e.preventDefault(); // Fix 1: block scroll/pan propagation
      const track = trackRef.current;
      if (!track) return;
      draggingRef.current = true;
      activePointerRef.current = e.pointerId;
      setValueFromClientX(e.clientX);
      try {
        track.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    [setValueFromClientX]
  );

  const onTrackPointerMove = useCallback(
    (e) => {
      if (!draggingRef.current || e.pointerId !== activePointerRef.current) return;
      e.preventDefault(); // Fix 1: prevent scroll during drag
      setValueFromClientX(e.clientX);
    },
    [setValueFromClientX]
  );

  const endTrackDrag = useCallback((e) => {
    if (e.pointerId !== activePointerRef.current) return;
    draggingRef.current = false;
    activePointerRef.current = null;
    const track = trackRef.current;
    if (track) {
      try {
        track.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
  }, []);

  // Fix 1: attach non-passive listener on mount so preventDefault is honoured
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const opts = { passive: false };
    el.addEventListener('touchstart', (e) => e.preventDefault(), opts);
    el.addEventListener('touchmove', (e) => e.preventDefault(), opts);
    return () => {
      el.removeEventListener('touchstart', (e) => e.preventDefault(), opts);
      el.removeEventListener('touchmove', (e) => e.preventDefault(), opts);
    };
  }, [phase]); // re-attach when feedback phase mounts

  const submitFeedback = async () => {
    setFeedbackError('');
    const uid = getDbUserId(sessionUser);
    const sid = sessionUser?.sessionId;
    const oid = sessionUser?.organizationId;
    const sidNorm = normStageId(stageRowId);

    if (!uid || !sid || !oid || !adminToken ) {
      setFeedbackError('You must be signed in to save feedback.');
      return;
    }
    if (!sidNorm) {
      setFeedbackError('Could not find your game session. Open the game from your invite link, then try again.');
      return;
    }

    setFeedbackSaving(true);
    try {
      const res = await fetch(`${backendBase}/welcomeStageFeedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: String(uid),
          sessionId: String(sid),
          organizationId: String(oid),
          stageId: sidNorm,
          feedback: feedbackValue,
        }),
      });
      const rawText = await res.text().catch(() => '');
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = {};
      }
      if (!res.ok || !data.success) {
        const apiMsg =
          (typeof data.message === 'string' && data.message) ||
          (typeof data.error === 'string' && data.error) ||
          (rawText && rawText.length < 280 ? rawText.trim() : '') ||
          `Request failed (${res.status})`;
        setFeedbackError(apiMsg);
        return;
      }
      setFeedbackAlreadySubmitted(true);
      setPhase('done');
    } catch (err) {
      console.error(err);
      setFeedbackError('Could not save feedback. Please try again.');
    } finally {
      setFeedbackSaving(false);
    }
  };

  return (
    <div className="thankyou-page">
      <div className="thankyou-dumbledore" aria-hidden="true">
        <img
          src={dumbledoreImg}
          alt=""
          className="thankyou-dumbledore-img"
          draggable={false}
        />
      </div>
      <div className="thankyou-content">
      {phase === 'scores' ? (
        <section className="thankyou-card thankyou-card--scores" aria-labelledby="thankyou-results-heading">
          <h1 id="thankyou-results-heading" className="thankyou-title">
            Your Hogwarts Trials are complete.
          </h1>

          <dl className="thankyou-scoreboard">
            <div className="thankyou-score-row">
              <dt>Spell Score</dt>
              <dd>{spellScore}</dd>
            </div>
            <div className="thankyou-score-row">
              <dt>Flight Score</dt>
              <dd>{flightScore}</dd>
            </div>
            <div className="thankyou-score-row thankyou-score-row--total">
              <dt>Total Score</dt>
              <dd>{totalPoints}</dd>
            </div>
          </dl>

          <p className="thankyou-performance" role="status">
            {perfText}
          </p>

          <div className="thankyou-actions">
            <button
              type="button"
              className="thankyou-primary"
              onClick={() => setPhase(feedbackAlreadySubmitted ? 'done' : 'feedback')}
            >
              {feedbackAlreadySubmitted ? 'Feedback Submitted' : 'Continue'}
            </button>
            <button
              type="button"
              className="thankyou-leaderboard-btn"
              onClick={() => navigate('/leaderboard')}
            >
              Leaderboard
            </button>
          </div>
        </section>
      ) : null}

      {phase === 'feedback' ? (
        <section className="thankyou-card thankyou-card--feedback thankyou-card--feedback-modal" aria-labelledby="thankyou-feedback-heading">
          <p className="thankyou-scroll-intro">
            The adventures of Hogwarts await your words… tell us how your magic felt!
          </p>

          <div className="thankyou-slider-scale" aria-hidden="true">
            <div className="thankyou-slider-scale-end">
              <span className="thankyou-slider-scale-num">1</span>
              <span className="thankyou-slider-scale-face" title="Not great">
                😐
              </span>
            </div>
            <div className="thankyou-slider-scale-end thankyou-slider-scale-end--right">
              <span className="thankyou-slider-scale-num">5</span>
              <span className="thankyou-slider-scale-face" title="Loved it">
                🤩
              </span>
            </div>
          </div>

          <div className="thankyou-slider-wrap">
            <div
              className="thankyou-slider-bubble"
              style={{ left: `${handlePercent}%` }}
              aria-hidden="true"
            >
              {feedbackValue}
            </div>

            <div
              ref={trackRef}
              className="thankyou-slider-track"
              role="slider"
              tabIndex={0}
              aria-label="Rate your experience from 1 to 5"
              aria-valuemin={FEEDBACK_MIN}
              aria-valuemax={FEEDBACK_MAX}
              aria-valuenow={feedbackValue}
              onPointerDown={onTrackPointerDown}
              onPointerMove={onTrackPointerMove}
              onPointerUp={endTrackDrag}
              onPointerCancel={endTrackDrag}
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  setFeedbackValue((v) => clampFeedbackStep(v - 1));
                } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  setFeedbackValue((v) => clampFeedbackStep(v + 1));
                }
              }}
            >
              <div className="thankyou-slider-track-bg" />
              <div className="thankyou-slider-track-fill" style={{ width: `${fillPercent}%` }} />
              <div className="thankyou-slider-handle" style={{ left: `${handlePercent}%` }}>
                <img src={charSlider} alt="" width={72} height={72} draggable={false} />
              </div>
            </div>
          </div>

          {feedbackError ? (
            <p className="thankyou-feedback-error" role="alert">
              {feedbackError}
            </p>
          ) : null}

          <div className="thankyou-modal-actions">
            <button type="button" className="thankyou-btn-cancel" onClick={() => setPhase('scores')}>
              Cancel
            </button>
            <button
              type="button"
              className="thankyou-btn-submit"
              disabled={feedbackSaving}
              onClick={submitFeedback}
            >
              {feedbackSaving ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </section>
      ) : null}

      {phase === 'done' ? (
        <section className="thankyou-card thankyou-card--done" aria-live="polite">
          <p className="thankyou-title thankyou-title--done">Your magic is heard… Hogwarts remembers!</p>
          <button
            type="button"
            className="thankyou-leaderboard-btn"
            onClick={() => navigate('/leaderboard')}
          >
            Leaderboard
          </button>
        </section>
      ) : null}
      </div>
    </div>
  );
}

export default ThankYou;
